import type { AppLayoutKind } from "@ui/layouts/layoutProvider";
import {
	agencyIdForBoardContext,
	clipContextForAppKind,
} from "@ui/molecules/AddToBoard/useAddToBoard";
import { addClipToBoard } from "@/client";
import type { MentionedVideoItem } from "~/modules/shared/chat/components/VideoMention";
import { mentionClipRange } from "~/modules/shared/chat/utils/videoMentionPlayback";
import { buildAddClipBody } from "./addToBoardSave";

export type AddMentionsToBoardResult = {
	added: number;
	/** Clips the board already held (HTTP 409). */
	duplicates: number;
	failed: number;
};

/**
 * Add each mentioned video to `boardId` as its own clip. There is no bulk
 * endpoint, so this is one request per mention.
 */
export async function addMentionsToBoard(params: {
	boardId: string;
	mentions: MentionedVideoItem[];
	appKind: AppLayoutKind;
	projectId?: string;
}): Promise<AddMentionsToBoardResult> {
	const context = clipContextForAppKind(params.appKind);
	const agencyId = agencyIdForBoardContext(context);

	const outcomes = await Promise.allSettled(
		params.mentions.map(async (mention) => {
			const { start, end } = mentionClipRange(mention);
			const result = await addClipToBoard({
				body: buildAddClipBody({
					videoId: mention.video_id ?? mention.id,
					boardId: params.boardId,
					moveClipTrim: { start, end },
					// Without a known source duration the padded window collapses onto
					// the clip itself, which the API accepts.
					videoDurationSeconds: mention.video_duration ?? end,
					context,
					projectId: params.projectId,
					agencyId,
					title: mention.title ?? "",
				}),
			});
			return result.response.status;
		}),
	);

	const result: AddMentionsToBoardResult = {
		added: 0,
		duplicates: 0,
		failed: 0,
	};

	for (const outcome of outcomes) {
		if (outcome.status === "rejected") {
			result.failed++;
		} else if (outcome.value === 200) {
			result.added++;
		} else if (outcome.value === 409) {
			result.duplicates++;
		} else {
			result.failed++;
		}
	}

	return result;
}
